import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';
import { formatDate } from '@/utils/dateFormat';

export default function SecretaryDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceCountdowns, setServiceCountdowns] = useState([]);
  const [showCountdowns, setShowCountdowns] = useState(false);
  // Pending staff approvals
  const [pendingStaff, setPendingStaff] = useState([]);
  // Order management state
  const [orders, setOrders] = useState([]);
  const [approvedMerchants, setApprovedMerchants] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderFormData, setOrderFormData] = useState({
    merchant_id: '',
    pickup_technician_id: '',
    items: [{ type: 'oil', brand: '', quantity: 1 }],
    total_amount: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, techniciansRes, countdownsRes, ordersRes, merchantsRes, pendingStaffRes] = await Promise.all([
        axios.get(`${API}/secretary/appointments`),
        axios.get(`${API}/secretary/technicians`),
        axios.get(`${API}/admin/customers-service-countdown`),
        axios.get(`${API}/orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/merchants/approved`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-staff`).catch(() => ({ data: [] }))
      ]);
      setAppointments(appointmentsRes.data);
      setTechnicians(techniciansRes.data);
      setServiceCountdowns(countdownsRes.data);
      setOrders(ordersRes.data);
      setApprovedMerchants(merchantsRes.data);
      setPendingStaff(pendingStaffRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const assignTechnician = async (appointmentId, technicianId) => {
    try {
      await axios.put(`${API}/secretary/appointments/${appointmentId}/assign?technician_id=${technicianId}`);
      toast.success('Technician assigned successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign technician');
    }
  };

  const verifyPayment = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/secretary/appointments/${appointmentId}/verify-payment`, {
        payment_status: status
      });
      toast.success(`Payment ${status === 'verified' ? 'verified' : 'marked as failed'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  // Pending Staff Approval Functions
  const approveStaff = async (userId) => {
    try {
      await axios.put(`${API}/admin/approve-staff/${userId}`);
      toast.success('Staff account approved successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve staff');
    }
  };

  const rejectStaff = async (userId) => {
    if (!confirm('Are you sure you want to reject this application?')) return;
    try {
      await axios.put(`${API}/admin/reject-staff/${userId}`);
      toast.success('Staff application rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject staff');
    }
  };

  // Order management functions
  const createOrder = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        merchant_id: orderFormData.merchant_id,
        pickup_technician_id: orderFormData.pickup_technician_id || null,
        items: orderFormData.items.filter(i => i.brand),
        total_amount: orderFormData.total_amount ? parseFloat(orderFormData.total_amount) : null,
        notes: orderFormData.notes || null
      };
      await axios.post(`${API}/orders`, payload);
      toast.success('Order created successfully');
      setOrderDialogOpen(false);
      setOrderFormData({
        merchant_id: '',
        pickup_technician_id: '',
        items: [{ type: 'oil', brand: '', quantity: 1 }],
        total_amount: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const addOrderItem = () => {
    setOrderFormData({
      ...orderFormData,
      items: [...orderFormData.items, { type: 'oil', brand: '', quantity: 1 }]
    });
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderFormData.items];
    newItems[index][field] = field === 'quantity' ? parseInt(value) || 1 : value;
    setOrderFormData({ ...orderFormData, items: newItems });
  };

  const removeOrderItem = (index) => {
    if (orderFormData.items.length > 1) {
      setOrderFormData({
        ...orderFormData,
        items: orderFormData.items.filter((_, i) => i !== index)
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const assignedAppointments = appointments.filter(a => a.status !== 'pending' && a.status !== 'cancelled');
  const pendingPayments = appointments.filter(a => 
    a.payment_method && 
    ['transfer', 'pos'].includes(a.payment_method) && 
    a.payment_status === 'pending'
  );

  return (
    <div className="min-h-screen bg-background" data-testid="secretary-dashboard">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-primary font-bold uppercase tracking-wide">Secretary Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <span className="text-sm font-medium text-muted-foreground hidden sm:inline">Hello, {user?.name}</span>
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="font-bold uppercase text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-primary text-white p-6 rounded-sm shadow-hard mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-heading font-bold mb-1">Appointment Scheduling</h1>
              <p className="text-sm opacity-90">Assign technicians to service appointments</p>
            </div>
            <Button 
              variant="outline"
              onClick={() => setShowCountdowns(!showCountdowns)}
              className="bg-white/10 text-white border-white/30 hover:bg-white/20 font-bold uppercase text-sm"
            >
              {showCountdowns ? 'Hide' : 'Show'} Service Countdown
            </Button>
          </div>
        </div>

        {/* Service Countdown Section (Collapsible) */}
        {showCountdowns && (
          <div className="mb-8 p-6 bg-white border-2 border-border rounded-sm">
            <h2 className="text-xl font-heading font-bold text-foreground mb-4">Customer Service Countdown</h2>
            
            {serviceCountdowns.length === 0 ? (
              <p className="text-muted-foreground">No customers with vehicles yet</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {serviceCountdowns.map((customer) => (
                  <div key={customer.customer_id} className="p-4 bg-accent rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{customer.customer_name}</span>
                      <span className="text-xs text-muted-foreground">{customer.customer_phone}</span>
                    </div>
                    
                    {customer.vehicles.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {customer.vehicles.map((vehicle) => (
                          <div 
                            key={vehicle.vehicle_id}
                            className={`p-3 rounded text-sm ${
                              vehicle.service_status === 'overdue' ? 'bg-red-100 text-red-800' :
                              vehicle.service_status === 'due_very_soon' ? 'bg-orange-100 text-orange-800' :
                              vehicle.service_status === 'due_soon' ? 'bg-yellow-100 text-yellow-800' :
                              vehicle.service_status === 'on_track' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{vehicle.make} {vehicle.model}</span>
                              {vehicle.days_until_service !== null ? (
                                <span className="font-bold">
                                  {vehicle.service_status === 'overdue' 
                                    ? `${Math.abs(vehicle.days_until_service)}d overdue` 
                                    : `${vehicle.days_until_service}d`}
                                </span>
                              ) : (
                                <span className="text-xs">No service yet</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No vehicles</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-3xl font-heading font-black text-yellow-600 mb-2">{pendingAppointments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Pending Assignments</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-3xl font-heading font-black text-primary mb-2">{assignedAppointments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Assigned This Week</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-3xl font-heading font-black text-green-600 mb-2">{pendingPayments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Payments to Verify</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-3xl font-heading font-black text-foreground mb-2">{technicians.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Available Technicians</div>
          </div>
        </div>

        {/* Pending Payments Section */}
        {pendingPayments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="text-green-600">💰</span> Payments Awaiting Verification
            </h2>
            <div className="space-y-4">
              {pendingPayments.map((appointment) => (
                <div key={appointment.id} className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{appointment.payment_method === 'transfer' ? '🏦' : '💳'}</span>
                        <span className="font-bold text-foreground">{appointment.payment_method === 'transfer' ? 'Bank Transfer' : 'POS Payment'}</span>
                      </div>
                      <p className="text-sm"><span className="text-muted-foreground">Service:</span> <span className="font-bold">{appointment.service_type}</span></p>
                      <p className="text-sm"><span className="text-muted-foreground">Date:</span> {formatDate(appointment.date)} at {appointment.time}</p>
                      <p className="text-sm"><span className="text-muted-foreground">Location:</span> {appointment.location}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => verifyPayment(appointment.id, 'verified')}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold"
                        data-testid="verify-payment-btn"
                      >
                        ✓ Verify Payment
                      </Button>
                      <Button
                        onClick={() => verifyPayment(appointment.id, 'failed')}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 font-bold"
                      >
                        ✗ Mark Failed
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Staff Approvals Section */}
        {pendingStaff.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></span>
              Pending Staff Approvals ({pendingStaff.length})
            </h2>
            <div className="space-y-4">
              {pendingStaff.map(person => (
                <div key={person.id} className="bg-white border-l-4 border-yellow-500 p-6 rounded-sm shadow-sm" data-testid="pending-staff-card">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-xl font-heading font-bold text-foreground">{person.name}</h4>
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                          person.role === 'technician' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {person.role}
                        </span>
                        {person.technician_type && (
                          <span className="text-xs font-bold px-2 py-1 rounded bg-gray-100 text-gray-700">
                            {person.technician_type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{person.email} • {person.phone}</p>
                      {person.state && (
                        <p className="text-sm text-muted-foreground mt-1">
                          📍 {person.lga}, {person.state}
                        </p>
                      )}
                      {person.address && (
                        <p className="text-sm text-muted-foreground">{person.address}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Applied: {new Date(person.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => approveStaff(person.id)} 
                        className="bg-green-600 hover:bg-green-700 text-white font-bold"
                        data-testid="approve-staff-btn"
                      >
                        ✓ Approve
                      </Button>
                      <Button 
                        onClick={() => rejectStaff(person.id)} 
                        variant="outline" 
                        className="border-red-300 text-red-600 hover:bg-red-50 font-bold"
                      >
                        ✗ Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Pending Appointments</h2>
        {pendingAppointments.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center mb-8">
            <p className="text-xl font-medium text-muted-foreground">No pending appointments</p>
          </div>
        ) : (
          <div className="space-y-4 mb-12">
            {pendingAppointments.map((appointment) => (
              <div key={appointment.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="pending-appointment-card">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <h3 className="text-xl font-heading font-bold text-foreground mb-2">{appointment.service_type}</h3>
                    <div className="space-y-1 text-sm font-medium">
                      <p><span className="text-muted-foreground">Date:</span> <span className="font-bold text-foreground">{formatDate(appointment.date)}</span></p>
                      <p><span className="text-muted-foreground">Time:</span> <span className="font-bold text-foreground">{appointment.time}</span></p>
                      <p><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{appointment.location}</span></p>
                      {!appointment.has_own_oil && (
                        <p className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                          <span className="font-bold text-yellow-800">⚠️ Provide Oil & Filter:</span>{' '}
                          <span className="text-yellow-700">
                            {appointment.needs_recommendation ? 'Customer needs recommendation' : appointment.preferred_oil_type}
                          </span>
                        </p>
                      )}
                      {appointment.notes && (
                        <p className="text-muted-foreground italic mt-2">&quot;{appointment.notes}&quot;</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-wide mb-2 block">Assign Technician</Label>
                    <Select onValueChange={(value) => assignTechnician(appointment.id, value)}>
                      <SelectTrigger className="input-field" data-testid="assign-technician-select">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {technicians.map(tech => (
                          <SelectItem key={tech.id} value={tech.id}>
                            {tech.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Assigned Appointments</h2>
        {assignedAppointments.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
            <p className="text-xl font-medium text-muted-foreground">No assigned appointments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assignedAppointments.map((appointment) => {
              const assignedTech = technicians.find(t => t.id === appointment.technician_id);
              return (
                <div key={appointment.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-heading font-bold text-foreground">{appointment.service_type}</h3>
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                          appointment.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          appointment.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {appointment.status}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-x-6 text-sm font-medium">
                        <p><span className="text-muted-foreground">Date:</span> <span className="font-bold text-foreground">{formatDate(appointment.date)}</span></p>
                        <p><span className="text-muted-foreground">Time:</span> <span className="font-bold text-foreground">{appointment.time}</span></p>
                        <p><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{appointment.location}</span></p>
                        <p><span className="text-muted-foreground">Technician:</span> <span className="font-bold text-primary">{assignedTech?.name || 'Unknown'}</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Merchant Orders Section */}
        <div className="mt-12 pt-8 border-t-2 border-border">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-foreground">Merchant Orders</h2>
            <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="secretary-create-order-btn">
                  + Create Order
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold text-foreground">Create Merchant Order</DialogTitle>
                </DialogHeader>
                <form onSubmit={createOrder} className="space-y-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Select Merchant *</Label>
                    <Select value={orderFormData.merchant_id} onValueChange={(v) => setOrderFormData({ ...orderFormData, merchant_id: v })}>
                      <SelectTrigger className="input-field mt-2">
                        <SelectValue placeholder="Choose merchant" />
                      </SelectTrigger>
                      <SelectContent>
                        {approvedMerchants.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.business_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Assign Pickup Technician</Label>
                    <Select value={orderFormData.pickup_technician_id} onValueChange={(v) => setOrderFormData({ ...orderFormData, pickup_technician_id: v })}>
                      <SelectTrigger className="input-field mt-2">
                        <SelectValue placeholder="Select technician (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {technicians.map(tech => (
                          <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-accent p-4 rounded-sm">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="font-bold uppercase text-sm tracking-wide">Order Items</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addOrderItem}>+ Add Item</Button>
                    </div>
                    {orderFormData.items.map((item, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Select value={item.type} onValueChange={(v) => updateOrderItem(index, 'type', v)}>
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="oil">Oil</SelectItem>
                            <SelectItem value="filter">Filter</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Brand name"
                          value={item.brand}
                          onChange={(e) => updateOrderItem(index, 'brand', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                          className="w-20"
                        />
                        {orderFormData.items.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeOrderItem(index)} className="text-red-600">×</Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Total Amount (₦)</Label>
                    <Input
                      type="number"
                      className="input-field mt-2"
                      placeholder="Enter total amount"
                      value={orderFormData.total_amount}
                      onChange={(e) => setOrderFormData({ ...orderFormData, total_amount: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Notes</Label>
                    <Input
                      className="input-field mt-2"
                      placeholder="Any special instructions"
                      value={orderFormData.notes}
                      onChange={(e) => setOrderFormData({ ...orderFormData, notes: e.target.value })}
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full h-12" disabled={!orderFormData.merchant_id}>
                    Create Order
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {orders.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
              <p className="text-xl font-medium text-muted-foreground">No orders yet</p>
              <p className="text-sm text-muted-foreground mt-2">Create an order to request supplies from merchants</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.slice(0, 10).map(order => (
                <div key={order.id} className="bg-white border-2 border-border p-4 rounded-sm">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'ready_for_pickup' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'picked_up' ? 'bg-orange-100 text-orange-800' :
                          order.status === 'delivered' ? 'bg-purple-100 text-purple-800' :
                          order.status === 'paid' ? 'bg-green-100 text-green-800' :
                          'bg-green-200 text-green-900'
                        }`}>
                          {order.status.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-muted-foreground">#{order.id.slice(0, 8)}</span>
                      </div>
                      <p className="font-bold text-foreground">{order.merchant_name}</p>
                      <div className="text-sm mt-1">
                        {order.items?.map((item, i) => (
                          <span key={i} className="mr-2">{item.quantity}x {item.brand} ({item.type})</span>
                        ))}
                      </div>
                      {order.total_amount && <p className="text-sm font-bold text-green-600 mt-1">₦{order.total_amount.toLocaleString()}</p>}
                      {order.technician_name && <p className="text-xs text-muted-foreground mt-1">Pickup: {order.technician_name}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}